/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';

import DateUtil from '../../utilities/DateUtil';
import { DateOfBirthPrecision } from '../../utilities/types';

export function getRelativeDateValue(intl, date, precision, dateKind, isTimeAgo, languageCode) {
  if (DateUtil.isToday(date)) {
    return intl.formatMessage({ id: 'ui-data.date.today.text' });
  } else if (DateUtil.isYesterday(date)) {
    return intl.formatMessage({ id: 'ui-data.date.yesterday.text' });
  } else if (isTimeAgo) {
    return DateUtil.timeAgo(date);
  } else if (dateKind === 'medium') {
    return DateUtil.formattedMediumDate(date, precision, languageCode);
  } else if (dateKind === 'long') {
    return DateUtil.formattedLongDate(date, precision, languageCode);
  } else if (precision !== 'P1D') {
    return DateUtil.formattedLongDate(date, precision, languageCode);
  } else {
    return '';
  }
}

function getFixedDate(date, precision, dateKind, languageCode) {
  if (dateKind === 'medium') {
    return DateUtil.formattedMediumDate(date, precision, languageCode);
  } else if (dateKind === 'long') {
    return DateUtil.formattedLongDate(date, precision, languageCode);
  } else {
    return '';
  }
}

function getDurationText(intl, startDateData, endDateData) {
  const hasStartDateData = !!startDateData.date;
  const hasEndDateData = !!endDateData.date;

  if (!hasStartDateData || !hasEndDateData) { return ''; }

  const durationData = DateUtil.fuzzyDateDurationData(startDateData, endDateData);

  let durationText = '';

  const monthText = durationData.months ? intl.formatMessage({ id: 'ui-data.duration.month.text' }, { digit1: durationData.months }) : null;
  if (monthText && durationData.years) {
    durationText = intl.formatMessage({ id: 'ui-data.duration.year-month.text' }, { digit1: durationData.years, string1: monthText });
  } else if (durationData.years) {
    durationText = intl.formatMessage({ id: 'ui-data.duration.year.text' }, { digit1: durationData.years });
  } else if (durationData.months) {
    durationText = monthText;
  } else if (durationData.weeks) {
    durationText = intl.formatMessage({ id: 'ui-data.duration.week.text' }, { digit1: durationData.weeks });
  } else if (durationData.days) {
    durationText = intl.formatMessage({ id: 'ui-data.duration.day.text' }, { digit1: durationData.days });
  }

  return durationText;
}

function getTimeFromNowText(intl, dateData, endDateData) {
  const today = (endDateData && endDateData.date) ? endDateData : {
    date: DateUtil.getToday(),
    precision: 'P1D',
  };

  if (!dateData.date) {
    return '';
  } else if (DateUtil.isToday(dateData.date, today.date)) {
    return intl.formatMessage({ id: 'ui-data.date.today.text' });
  } else if (DateUtil.isYesterday(dateData.date, today.date)) {
    return intl.formatMessage({ id: 'ui-data.date.yesterday.text' });
  } else if (DateUtil.isTomorrow(dateData.date, today.date)) {
    return intl.formatMessage({ id: 'ui-data.date.tomorrow.text' });
  }

  const durationCounts = DateUtil.durationCounts(dateData, today);
  if (durationCounts.months <= 2) {
    today.date = DateUtil.subtract(today.date, 1, 'days');
  }

  const durationText = getDurationText(intl, dateData, today);
  let durationExactnessText = '';
  if (dateData.precision === today.precision && dateData.precision === 'P1D' && durationCounts.days <= 15) {
    durationExactnessText = durationText;
  } else if (dateData.precision === today.precision) {
    durationExactnessText = intl.formatMessage({ id: 'ui-data.duration.about.text' }, { string1: durationText });
  } else {
    durationExactnessText = intl.formatMessage({ id: 'ui-data.duration.up-to.text' }, { string1: durationText });
  }

  if (DateUtil.isInFuture(dateData.date)) {
    return intl.formatMessage({ id: 'ui-data.time-from-now.future-prefix.text' }, { string1: durationExactnessText });
  } else {
    return intl.formatMessage({ id: 'ui-data.time-from-now.past-suffix.text' }, { string1: durationExactnessText });
  }
}

const LocalizedAge = (props) => {
  const ageData = DateUtil.ageData(props.dateOfBirth, props.today);
  let id = '';
  let age = 0;

  if (ageData.years >= 1) {
    id = 'ui-data.age.years-old.text';
    age = ageData.years;
  } else if (ageData.months >= 1) {
    id = 'ui-data.age.months-old.text';
    age = ageData.months;
  } else if (ageData.days >= 1) {
    id = 'ui-data.age.days-old.text';
    age = ageData.days;
  } else if (ageData.months < 0) {
    id = 'ui-data.age.months-due.text';
    age = Math.abs(ageData.months);
  } else if (ageData.days < 0) {
    id = 'ui-data.age.days-due.text';
    age = Math.abs(ageData.days);
  } else {
    id = 'ui-data.age.before-old.text';
  }

  if (age === 0) {
    return <FormattedMessage id="ui-data.age.zero.text" />;
  } else {
    return <FormattedMessage id={id} values={{ digit1: age }} />;
  }
};
LocalizedAge.propTypes = {
  dateOfBirth: PropTypes.string,
  intl: PropTypes.object,
  today: PropTypes.string,
};

const LocalizedBirthDateWithAge = (props) => {
  if (props.precision === DateOfBirthPrecision.P1Y) {
    const year = props.intl.formatMessage({
      id: 'ui-date.age.yearonly.born.text',
    }, {
      string1: DateUtil.getYear(props.profile.date_of_birth),
    });

    return <span>{ year }</span>;
  }

  const formattedBirthDate = props.isMediumFormat
    ? DateUtil.formattedMediumDate(props.profile.date_of_birth, props.precision, props.intl.locale)
    : DateUtil.formattedLongDate(props.profile.date_of_birth, props.precision, props.intl.locale);

  return <span>{formattedBirthDate} ({<LocalizedAge dateOfBirth={props.profile.date_of_birth} precision={props.precision} intl={props.intl} today={props.today} />})</span>;
};
LocalizedBirthDateWithAge.propTypes = {
  profile: PropTypes.object,
  intl: PropTypes.object,
  today: PropTypes.string,
  precision: PropTypes.string,
  isMediumFormat: PropTypes.bool,
};

LocalizedBirthDateWithAge.defaultProps = {
  precision: DateOfBirthPrecision.P1D,
  isMediumFormat: false,
};

const LocalizedFuzzyDateDuration = (props) => {
  let hasEndDate = _.get(props, 'endDateData.date', '') !== '';
  const hasStartDate = _.get(props, 'startDateData.date', '') !== '';

  if (!hasEndDate && !hasStartDate) { return <span />; }

  const endDate = props.isCurrent ? DateUtil.today() : '';
  let endDateData = {};

  if (props.isCurrent) {
    endDateData = { date: endDate, precision: 'P1D' };
    hasEndDate = true;
  } else if (hasEndDate) {
    endDateData = props.endDateData;
  } else {
    endDateData = { date: '', precision: 'P1D' };
  }

  const durationData = DateUtil.fuzzyDateDurationData(props.startDateData, endDateData);
  let durationText = '';

  const monthText = durationData.months ? props.intl.formatMessage({ id: 'ui-data.duration.month.text' }, { digit1: durationData.months }) : null;
  if (monthText && durationData.years) {
    durationText = props.intl.formatMessage({ id: 'ui-data.duration.year-month.text' }, { digit1: durationData.years, string1: monthText });
  } else if (durationData.years) {
    durationText = props.intl.formatMessage({ id: 'ui-data.duration.year.text' }, { digit1: durationData.years });
  } else if (durationData.months) {
    durationText = monthText;
  } else if (durationData.weeks) {
    durationText = props.intl.formatMessage({ id: 'ui-data.duration.week.text' }, { digit1: durationData.weeks });
  } else if (durationData.days) {
    durationText = props.intl.formatMessage({ id: 'ui-data.duration.day.text' }, { digit1: durationData.days });
  }

  let prefixId = '';

  if (durationData.isExact || !hasEndDate) {
    if (durationData.isRounded) {
      prefixId = 'ui-data.duration.about.text';
    } else {
      return <span>{durationText}</span>;
    }
  } else {
    prefixId = durationData.isSameFuzzy ? 'ui-data.duration.about.text' : 'ui-data.duration.up-to.text';
  }

  return <FormattedMessage id={prefixId} values={{ string1: durationText }} />;
};
LocalizedFuzzyDateDuration.propTypes = {
  intl: PropTypes.object.isRequired,
  startDateData: PropTypes.object.isRequired,
  endDateData: PropTypes.object,
  isCurrent: PropTypes.bool.isRequired,
};

const LocalizedHealthEntryDuration = (props) => {
  let hasEndDate = _.get(props, 'endDateData.date', '') !== '';
  const hasStartDate = _.get(props, 'startDateData.date', '') !== '';

  if (!hasEndDate && !hasStartDate) { return <span />; }

  let endDateData = {};

  if (props.isCurrent) {
    endDateData = { date: DateUtil.today(), precision: 'P1D' };
    hasEndDate = true;
  } else if (hasEndDate) {
    endDateData = props.endDateData;
  } else {
    endDateData = { date: '', precision: 'P1D' };
  }

  const durationData = DateUtil.fuzzyDateDurationData(props.startDateData, endDateData);
  const durationText = getDurationText(props.intl, props.startDateData, endDateData);

  let durationExactnessText = '';

  if (durationData.isExact) {
    durationExactnessText = durationText;
  } else if (!hasEndDate) {
    if (durationData.isRounded) {
      durationExactnessText = props.intl.formatMessage({ id: 'ui-data.duration.about.text' }, { string1: durationText });
    } else {
      durationExactnessText = durationText;
    }
  } else if (durationData.isSameFuzzy) {
    durationExactnessText = props.intl.formatMessage({ id: 'ui-data.duration.about.text' }, { string1: durationText });
  } else {
    durationExactnessText = props.intl.formatMessage({ id: 'ui-data.duration.up-to.text' }, { string1: durationText });
  }

  return <FormattedMessage id="ui-data.duration.for-prefix.text" values={{ string1: durationExactnessText }} />;
};
LocalizedHealthEntryDuration.propTypes = {
  intl: PropTypes.object.isRequired,
  startDateData: PropTypes.object.isRequired,
  endDateData: PropTypes.object,
  isCurrent: PropTypes.bool.isRequired,
};


const LocalizedHealthEntryDateSummaryDate = (props) => {
  const hasEndDate = _.get(props, 'endDateData.date', '') !== '';
  const hasStartDate = _.get(props, 'startDateData.date', '') !== '';

  if (!hasEndDate && !hasStartDate) { return <span />; }

  const startDateData = _.get(props, 'startDateData', null) || { date: '', precision: '' };
  const endDateData = _.get(props, 'endDateData', null) || { date: '', precision: '' };

  const startedText = DateUtil.formattedMediumDate(startDateData.date, startDateData.precision, props.languageCode);
  const endedText = DateUtil.formattedMediumDate(endDateData.date, endDateData.precision, props.languageCode);


  // healthEntry is current
  if (props.isCurrent) {
    return <FormattedMessage id="ui-data.date.started.text" values={{ string1: startedText }} />;

  // healthEntry is in past single day
  } else if (!props.isPeriod) {
    return <span>{startedText}</span>;

  // healthEntry is in past multiple days
  } else if (hasEndDate && hasStartDate) {
    return <span>{startedText} - {endedText}</span>;
  } else if (hasStartDate) {
    return <FormattedMessage id="ui-data.date.started.text" values={{ string1: startedText }} />;
  } else if (hasEndDate) {
    return <FormattedMessage id="ui-data.date.ended.text" values={{ string1: endedText }} />;
  } else {
    return <span>{startedText}</span>;
  }
};
LocalizedHealthEntryDateSummaryDate.propTypes = {
  intl: PropTypes.object.isRequired,
  startDateData: PropTypes.object.isRequired,
  endDateData: PropTypes.object,
  isCurrent: PropTypes.bool.isRequired,
  isPeriod: PropTypes.bool.isRequired,
  languageCode: PropTypes.string,
};


const LocalizedHealthEntryDateSummaryContext = (props) => {
  const hasEndDate = _.get(props, 'endDateData.date', '') !== '';
  const hasStartDate = _.get(props, 'startDateData.date', '') !== '';
  const isStartDateUnsure = _.get(props, 'startDateData.unsure', false);
  const isEndDateUnsure = _.get(props, 'endDateData.unsure', false);
  const dateData = hasEndDate && !props.isCurrent ? props.endDateData : props.startDateData;
  const timeFromNowText = getTimeFromNowText(props.intl, dateData, props.nowDateData);
  const isDuration = props.isPeriod && hasEndDate && hasStartDate;

  let durationText = '';

  if (isStartDateUnsure && isEndDateUnsure) {
    return <FormattedMessage id="ui-data.date.unsure-when.text" />;
  } else if (!hasEndDate && !hasStartDate) {
    return <span />;
  } else if (isStartDateUnsure) {
    return <FormattedMessage id="ui-data.date.unsure-started.text" />;
  } else if (isEndDateUnsure) {
    return <FormattedMessage id="ui-data.date.unsure-ended.text" />;
  } else if (props.isCurrent) {
    durationText = timeFromNowText;
  } else if (isDuration) {
    return <LocalizedHealthEntryDuration {...props} />;
  } else {
    durationText = timeFromNowText;
  }

  return <span>{durationText}</span>;
};
LocalizedHealthEntryDateSummaryContext.propTypes = {
  intl: PropTypes.object.isRequired,
  startDateData: PropTypes.object.isRequired,
  endDateData: PropTypes.object,
  nowDateData: PropTypes.object,
  isCurrent: PropTypes.bool.isRequired,
  isPeriod: PropTypes.bool.isRequired,
};


const LocalizedTimeFromNow = (props) => {
  const dateData = props.date ? {
    date: props.date,
    precision: 'P1D',
  } : props.dateData;
  const timeFromNowText = getTimeFromNowText(props.intl, dateData, props.nowDateData);
  return <span>{timeFromNowText}</span>;
};
LocalizedTimeFromNow.propTypes = {
  intl: PropTypes.object.isRequired,
  dateData: PropTypes.object,
  nowDateData: PropTypes.object,
  date: PropTypes.string,
};

const LocalizedFuzzyDateRange = (props) => {
  const startDate = _.isObject(props.startDateData) ? DateUtil.formattedMediumDate(props.startDateData.date, props.startDateData.precision, props.languageCode) : '';
  const endDate = _.isObject(props.endDateData) ? DateUtil.formattedMediumDate(props.endDateData.date, props.endDateData.precision, props.languageCode) : '';

  let rangeText = '';

  if (props.isCurrent) {
    if (startDate) {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.current-with-start.text' }, { string1: startDate });
    } else {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.current.text' });
    }
  } else if (startDate === '' && endDate === '') {
    rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past.text' });
  } else if (!props.isPeriod) {
    rangeText = startDate;
  } else if (startDate && endDate) {
    rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past-with-start-end.text' }, { string1: startDate, string2: endDate });
  } else if (startDate) {
    rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past-with-start.text' }, { string1: startDate });
  } else if (endDate) {
    rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past-with-end.text' }, { string1: endDate });
  } else {
    rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past.text' });
  }

  return <span>{rangeText}</span>;
};
LocalizedFuzzyDateRange.propTypes = {
  intl: PropTypes.object,
  startDateData: PropTypes.object,
  endDateData: PropTypes.object,
  isCurrent: PropTypes.bool,
  isPeriod: PropTypes.bool.isRequired,
  languageCode: PropTypes.string,
};

const LocalizedRelativeDate = (props) => {
  const date = getRelativeDateValue(props.intl, props.date, props.precision, props.dateKind, props.isTimeAgo, props.languageCode);

  if (typeof props.children === 'function') {
    return props.children(date);
  } else {
    return <span>{date}</span>;
  }
};
LocalizedRelativeDate.propTypes = {
  intl: PropTypes.object.isRequired,
  date: PropTypes.string,
  precision: PropTypes.string,
  dateKind: PropTypes.string.isRequired,
  isTimeAgo: PropTypes.bool,
  children: PropTypes.any,
  languageCode: PropTypes.string,
};

const LocalizedFixedDate = (props) => (
  <span>{getFixedDate(props.date, props.precision, props.dateKind, props.languageCode)}</span>
);
LocalizedFixedDate.propTypes = {
  intl: PropTypes.object.isRequired,
  date: PropTypes.string,
  precision: PropTypes.string,
  dateKind: PropTypes.string,
  languageCode: PropTypes.string,
};

const LocalizedHealthEntryListDate = (props) => {
  const startDate = _.isObject(props.startDateData) ? DateUtil.formattedMediumDate(props.startDateData.date, props.startDateData.precision, props.languageCode) : '';
  const endDate = _.isObject(props.endDateData) ? DateUtil.formattedMediumDate(props.endDateData.date, props.endDateData.precision, props.languageCode) : '';

  let rangeText = '';

  if (props.isCurrent) {
    if (startDate) {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.current-with-start.text' }, { string1: startDate });
    } else {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.current.text' });
    }
  } else if (!startDate && !endDate) {
    if (props.isOnlyPast) {
      rangeText = props.intl.formatMessage({ id: 'ui-data.date.not-specified.text' });
    } else {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past.text' });
    }
  } else if (!props.isPeriod) {
    if (startDate) {
      rangeText = startDate;
    } else if (props.isOnlyPast) {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past.text' });
    } else {
      rangeText = props.intl.formatMessage({ id: 'ui-data.healthentryperiod.past.text' });
    }
  } else if (startDate && endDate) {
    rangeText = `${startDate} – ${endDate}`;
  } else if (startDate) {
    rangeText = `${startDate} – ${props.intl.formatMessage({ id: 'ui-data.date.notspecified.text' })}`;
  } else if (endDate) {
    rangeText = `${props.intl.formatMessage({ id: 'ui-data.date.notspecified.text' })} – ${endDate}`;
  }

  return <span>{rangeText}</span>;
};
LocalizedHealthEntryListDate.propTypes = {
  intl: PropTypes.object,
  startDateData: PropTypes.object,
  endDateData: PropTypes.object,
  isCurrent: PropTypes.bool.isRequired,
  isPeriod: PropTypes.bool.isRequired,
  isOnlyPast: PropTypes.bool.isRequired,
  languageCode: PropTypes.string,
};

const Age = injectIntl(LocalizedAge);
const BirthDateWithAge = injectIntl(LocalizedBirthDateWithAge);
const RelativeDate = injectIntl(LocalizedRelativeDate);
const FixedDate = injectIntl(LocalizedFixedDate);
const FuzzyDateDuration = injectIntl(LocalizedFuzzyDateDuration);
const FuzzyDateRange = injectIntl(LocalizedFuzzyDateRange);
const TimeFromNow = injectIntl(LocalizedTimeFromNow);
const HealthEntryDuration = injectIntl(LocalizedHealthEntryDuration);
const HealthEntryDateSummaryDate = injectIntl(LocalizedHealthEntryDateSummaryDate);
const HealthEntryDateSummaryContext = injectIntl(LocalizedHealthEntryDateSummaryContext);
const HealthEntryListDate = injectIntl(LocalizedHealthEntryListDate);

const fixedLongDate = (date, precision, languageCode) => {
  return getFixedDate(date, precision, 'long', languageCode);
};

const fixedMediumDate = (date, precision, languageCode) => {
  return getFixedDate(date, precision, 'medium', languageCode);
};

const fixedShortDate = (date, precision, languageCode) => {
  return getFixedDate(date, precision, 'short', languageCode);
};

export {
  Age,
  BirthDateWithAge,
  RelativeDate,
  FixedDate,
  FuzzyDateDuration,
  FuzzyDateRange,
  TimeFromNow,
  HealthEntryDuration,
  HealthEntryDateSummaryDate,
  HealthEntryDateSummaryContext,
  HealthEntryListDate,
  fixedLongDate,
  fixedMediumDate,
  fixedShortDate,
};
